import { type FC } from "react";
import { FormattedMessage, type IntlShape } from "react-intl";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";

import { EmptyState } from "@/components/empty-state";

import { CartonCountSection, type ResolutionItem, ResolutionSection, TotalItemsSection } from "../components";

export interface BlockedDetail {
	count: number;
	resolutions: ResolutionItem[];
}

interface OptimalProductionBlockedProps {
	readonly details: BlockedDetail[];
	readonly count: number;
}

const toActionDescription = (
	actionCode: string,
	entityToBeModified: string,
	entitiesToBeAssociated: string,
	intl: IntlShape,
): string => {
	switch (actionCode) {
		case "AssignMaterialToProductionGroup": {
			const action = intl.formatMessage({ id: "Assign Corrugates", defaultMessage: "Assign z-Folds" });
			const target = intl.formatMessage({ id: "to Production Group", defaultMessage: "to Production Group" });
			return `${action} ${entitiesToBeAssociated} ${target} ${entityToBeModified}`;
		}
		case "AssignMachineToProductionGroup": {
			const action = intl.formatMessage({ id: "Assign Machines", defaultMessage: "Assign Machines" });
			const target = intl.formatMessage({ id: "to Production Group", defaultMessage: "to Production Group" });
			return `${action} ${entitiesToBeAssociated} ${target} ${entityToBeModified}`;
		}
		case "LoadMaterial": {
			const action = intl.formatMessage({ id: "Load", defaultMessage: "Load" });
			const target = intl.formatMessage({ id: "on machine", defaultMessage: "on machine" });
			return `${action} ${entityToBeModified} ${target} ${entitiesToBeAssociated}`;
		}
		case "EnableMachineGroup": {
			const action = intl.formatMessage({ id: "Enable Machine Group", defaultMessage: "Enable Machine Group" });
			return `${action}: ${entitiesToBeAssociated}`;
		}
		case "ReplenishMaterial": {
			const action = intl.formatMessage({ id: "Replenish", defaultMessage: "Replenish" });
			const target = intl.formatMessage({ id: "on machine", defaultMessage: "on machine" });
			return `${action} ${entityToBeModified} ${target} ${entitiesToBeAssociated}`;
		}
		default:
			return `${intl.formatMessage({ id: "Unknown actionCode:", defaultMessage: "Unknown actionCode:" })} ${actionCode}`;
	}
};

const OptimalProductionBlocked: FC<OptimalProductionBlockedProps> = ({ details, count }) => {
	return (
		<TableContainer sx={{ height: "100%" }}>
			<Table stickyHeader>
				<TableHead>
					<TableRow>
						<TableCell>
							<TotalItemsSection count={count} />
						</TableCell>
						<TableCell>
							<FormattedMessage id="Action to Take" defaultMessage="Action to Take" />
						</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{details.length === 0 && (
						<TableRow>
							<TableCell colSpan={2}>
								<EmptyState>
									<FormattedMessage id="No blocked items" defaultMessage="No blocked items" />
								</EmptyState>
							</TableCell>
						</TableRow>
					)}
					{details.map((item) => (
						<TableRow key={`${item.count}-${item.resolutions.map((r) => r.type).join("-")}`}>
							<TableCell>
								<CartonCountSection count={item.count} />
							</TableCell>
							<TableCell>
								<ResolutionSection resolutions={item.resolutions} getActionDescription={toActionDescription} />
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</TableContainer>
	);
};

export default OptimalProductionBlocked;
